import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import ButtonSecondarySmall from "../../../DesignSystem/Buttons/ButtonSecondarySmall";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import OutlinedStatusButton from "../../../DesignSystem/Buttons/OutlinedStatusButtons";
import Tooltip from "../../../DesignSystem/Tooltip/Tooltip";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { subscribeWebsites } from "../services/websiteService";
import { importPrintingCharges, subscribePrintingCharges } from "../services/printingChargeService";
import { subscribeEmailLicences } from "../services/emailLicenceService";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/information.svg";
import { ReactComponent as WarningIcon } from "../../../../assets/icons/warning-yellow.svg";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow-right.svg";
import WebsiteChargesTable from "../components/WebsiteChargesTable";
import { downloadWebsiteChargesExcel } from "../utils/exportWebsiteChargesExcel";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import Banner from "../../../DesignSystem/Banner/Banner";
import { getWebsiteEstimatedTurnaroundDate, isWebsiteOverdue, isWebsiteTimelineExtended } from "../utils/websiteTimeline";
import { parsePrintingChargesWorkbook } from "../utils/importPrintingChargesExcel";
import { auth } from "../../../../backend/firebaseConfig";
import { getLiveWebsiteChargeFlag } from "../utils/websiteLiveChargeFlag";
// import "../../../../css/Marketing/FinancialPromotions/FinancialPromotions.css";

const DASHBOARD_TABS = [
  { id: "websites", label: "Websites in Pipeline" },
  { id: "charges", label: "Website Charges" },
  { id: "printing", label: "Printing Charges" },
  { id: "licences", label: "Email Licences" },
];

const STATUS_COLOURS = {
  Working: "blue",
  "Get in Contact": "red",
  Design: "yellow",
  Branding: "brandingOrange",
  "Content Writing": "orange",
  "Content Review": "crimson",
  Review: "orange600",
  "Internal Review": "orange600",
  "Adviser Review": "purple",
  Development: "blue",
  Live: "green",
  NPW: "grey",
  NPM: "grey",
  "On Hold": "crimson",
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "-";
  const dateValue = typeof value?.toDate === "function" ? value.toDate() : value;
  const parsedDate = dayjs(dateValue);
  return parsedDate.isValid()
    ? parsedDate.format(includeTime ? "DD MMM YY, HH:mm" : "DD MMM YY")
    : "-";
};

const getWebsiteActivityTime = (website) => {
  const value =
    website.updatedAt ||
    website.updatedAtMs ||
    website.createdAt ||
    website.createdAtMs;
  const dateValue = typeof value?.toDate === "function" ? value.toDate() : value;
  const timestamp = dayjs(dateValue).valueOf();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const getPipelineDates = (website) =>
  String(website.status || "").trim().toLowerCase() === "live"
    ? [...new Set((website.domains || []).map((domain) => domain.design?.liveDate).filter(Boolean))].sort()
    : [getWebsiteEstimatedTurnaroundDate(website)];

const getPipelineDateTime = (website) => {
  const dates = getPipelineDates(website);
  const date = dates.length ? dayjs(dates[dates.length - 1]) : null;
  return date?.isValid() ? date.valueOf() : 0;
};

const matchesSearch = (record, searchTerm, additionalValues = []) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return true;
  return [JSON.stringify(record), ...additionalValues]
    .join(" ")
    .toLowerCase()
    .includes(term);
};

const MarketingWebsitesDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const initialTab = DASHBOARD_TABS.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : location.state?.activeTab || "websites";
  const { userPermissions } = useUserPermissions();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [printingCharges, setPrintingCharges] = useState([]);
  const [printingLoading, setPrintingLoading] = useState(true);
  const [printingSort, setPrintingSort] = useState({ key: "orderDate", direction: "desc" });
  const [emailLicences, setEmailLicences] = useState([]);
  const [licencesLoading, setLicencesLoading] = useState(true);
  const [licenceSort, setLicenceSort] = useState({ key: "orderDate", direction: "desc" });
  const [importingPrinting, setImportingPrinting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const printingFileInputRef = useRef(null);
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || "",
  );
  const [pipelineSort, setPipelineSort] = useState({
    key: "activity",
    direction: "desc",
  });
  const canViewExcel = Array.isArray(userPermissions) && userPermissions.some(
    (permission) => String(permission || "").trim().toLowerCase().includes("administrator") || String(permission || "").trim().toLowerCase().includes("finance"),
  );
  const canViewChargeTotals =
    Array.isArray(userPermissions) &&
    userPermissions.some((permission) => {
      const normalisedPermission = String(permission || "")
        .trim()
        .toLowerCase();
      return ["administrator", "finance admin"].includes(
        normalisedPermission,
      );
    });

  useEffect(() => {
    if (!successMessage) return undefined;
    const timeoutId = window.setTimeout(() => setSuccessMessage(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);
  const togglePipelineSort = (key) => {
    setPipelineSort((current) => ({
      key,
      direction:
        current.key === key
          ? current.direction === "asc" ? "desc" : "asc"
          : key === "adviser" ? "asc" : "desc",
    }));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`${location.pathname}?tab=${tabId}`, {
      replace: true,
      state: { ...(location.state || {}), activeTab: tabId },
    });
  };

  const togglePrintingSort = (key) => {
    setPrintingSort((current) => ({
      key,
      direction: current.key === key
        ? current.direction === "asc" ? "desc" : "asc"
        : key === "orderDate" ? "desc" : "asc",
    }));
  };

  const sortedPrintingCharges = printingCharges
    .filter((charge) => matchesSearch(charge, searchTerm, [formatDate(charge.orderDate)]))
    .sort((left, right) => {
    let comparison;
    if (printingSort.key === "orderDate") {
      const leftDate = dayjs(left.orderDate);
      const rightDate = dayjs(right.orderDate);
      comparison = (leftDate.isValid() ? leftDate.valueOf() : 0)
        - (rightDate.isValid() ? rightDate.valueOf() : 0);
    } else if (printingSort.key === "orderItems") {
      comparison = (left.orderItems || []).join(", ").localeCompare(
        (right.orderItems || []).join(", ")
      );
    } else if (printingSort.key === "amountToCharge") {
      comparison = Number(left.amountToCharge || 0) - Number(right.amountToCharge || 0);
    } else {
      comparison = String(left[printingSort.key] || "").localeCompare(
        String(right[printingSort.key] || "")
      );
    }
    return printingSort.direction === "asc" ? comparison : -comparison;
    });

  const toggleLicenceSort = (key) => setLicenceSort((current) => ({
    key,
    direction: current.key === key ? current.direction === "asc" ? "desc" : "asc" : key === "orderDate" ? "desc" : "asc",
  }));
  const sortedEmailLicences = emailLicences
    .filter((licence) => matchesSearch(licence, searchTerm, [formatDate(licence.orderDate), formatDate(licence.chargeFromDate || licence.orderDate)]))
    .sort((left, right) => {
    let comparison;
    if (licenceSort.key === "orderDate") comparison = dayjs(left.orderDate).valueOf() - dayjs(right.orderDate).valueOf();
    else if (licenceSort.key === "chargeFromDate") comparison = dayjs(left.chargeFromDate || left.orderDate).valueOf() - dayjs(right.chargeFromDate || right.orderDate).valueOf();
    else if (["numberOfLicences", "monthlyAmount"].includes(licenceSort.key)) comparison = Number(left[licenceSort.key] || 0) - Number(right[licenceSort.key] || 0);
    else comparison = String(left[licenceSort.key] || "").localeCompare(String(right[licenceSort.key] || ""));
    return licenceSort.direction === "asc" ? comparison : -comparison;
    });

  const printingChargesTotal = sortedPrintingCharges.reduce((total, charge) => {
    const amount = Number(charge.amountToCharge);
    return total + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const emailLicencesTotal = sortedEmailLicences.reduce((total, licence) => {
    const amount = Number(licence.monthlyAmount);
    return total + (Number.isFinite(amount) ? amount : 0);
  }, 0);

  const filteredWebsites = websites.filter((website) => matchesSearch(website, searchTerm, [
    formatDate(website.dateStarted),
    formatDate(website.dateAdviserInformed),
    formatDate(website.estimatedTurnaroundDate),
    ...getPipelineDates(website).map((date) => formatDate(date)),
    formatDate(website.updatedAt, true),
  ]));

  const pipelineWebsites = filteredWebsites
    .filter((website) => String(website.status || "").trim())
    .sort((left, right) => {
    const getStatusRank = (website) => {
      const status = String(website.status || "").trim().toLowerCase();
      if (["npw", "npm"].includes(status)) return 2;
      if (status === "live") return 1;
      return 0;
    };
    const statusRankComparison = getStatusRank(left) - getStatusRank(right);
    if (statusRankComparison !== 0) return statusRankComparison;

    let comparison = 0;
    if (pipelineSort.key === "adviser") {
      comparison = String(left.adviserName || "").localeCompare(
        String(right.adviserName || ""),
      );
    } else if (pipelineSort.key === "turnaround") {
      comparison =
        getPipelineDateTime(left) - getPipelineDateTime(right);
    } else {
      comparison = getWebsiteActivityTime(left) - getWebsiteActivityTime(right);
    }

    return pipelineSort.direction === "asc" ? comparison : -comparison;
    });

  useEffect(
    () =>
      subscribeWebsites(
        (items) => {
          setWebsites(items);
          setLoading(false);
        },
        () => {
          setError("We couldn't load the website pipeline.");
          setLoading(false);
        },
      ),
    [],
  );

  useEffect(
    () => subscribeEmailLicences(
      (items) => { setEmailLicences(items); setLicencesLoading(false); },
      () => { setError("We couldn't load the email licences."); setLicencesLoading(false); },
    ),
    [],
  );

  useEffect(
    () => subscribePrintingCharges(
      (items) => { setPrintingCharges(items); setPrintingLoading(false); },
      () => { setError("We couldn't load the printing charges."); setPrintingLoading(false); },
    ),
    [],
  );

  const openWebsite = (websiteId) =>
    navigate(`/department/marketing/design⁴/${websiteId}`, { state: { returnTab: activeTab } });
  const openPrintingCharge = (printingId) =>
    navigate(`/department/marketing/printing/${printingId}`, { state: { returnTab: "printing" } });
  const openEmailLicence = (licenceId) => navigate(`/department/marketing/email-licences/${licenceId}`, { state: { returnTab: "licences" } });

  const handleDownloadExcel = async () => {
    try {
      await downloadWebsiteChargesExcel(websites, printingCharges, emailLicences);
    } catch (downloadError) {
      setError(downloadError.message || "We couldn't download the website charges workbook.");
    }
  };

  const handlePrintingExcelImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setSuccessMessage("");
    setImportingPrinting(true);
    try {
      const charges = await parsePrintingChargesWorkbook(file);
      const count = await importPrintingCharges({
        charges,
        actor: {
          uid: auth.currentUser?.uid || "",
          email: auth.currentUser?.email || localStorage.getItem("userEmail") || "",
          displayName: auth.currentUser?.displayName || "",
        },
      });
      setSuccessMessage(`${count} printing charge${count === 1 ? "" : "s"} imported successfully.`);
    } catch (importError) {
      setError(importError.message || "We couldn't import the printing charges workbook.");
    } finally {
      setImportingPrinting(false);
    }
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Marketing &amp; Design Charges Dashboard</title>
      </Helmet>
      <Sidebar />
      <main className="Main-Container table-contained-page website-page website-dashboard-page column gap-xl">
        <Breadcrumb
          items={[
            { label: "Marketing", to: APP_ROUTES.DEPARTMENT_MARKETING },
            { label: "design⁴" },
          ]}
        />

        <header className="row space-between">
          <div className="FP_H3">Marketing &amp; Design Charges Dashboard</div>
          <div className="row gap-s">
            <div className="row align-center gap-s">
              <SearchField
                id="marketing-charges-search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={`Search ${DASHBOARD_TABS.find((tab) => tab.id === activeTab)?.label || "dashboard"}`}
              />
              {/* <button type="button" className="table-row-action" onClick={handleDownloadExcel}
              aria-label="Download website charges Excel workbook" title="Download Excel">
              <ExcelIcon aria-hidden />
            </button> */}
              {/* {activeTab === "printing" && (
                <>
                  <input
                    ref={printingFileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={handlePrintingExcelImport}
                  />
                  <ButtonSecondarySmall
                    label={importingPrinting ? "Importing…" : "Upload Excel"}
                    disabled={importingPrinting}
                    onClick={() => printingFileInputRef.current?.click()}
                  />
                </>
              )} */}
              <ButtonSecondarySmall
                label="Add New Entry"
                onClick={() =>
                  navigate(
                    activeTab === "printing"
                      ? APP_ROUTES.MARKETING_PRINTING_NEW
                      : activeTab === "licences"
                        ? APP_ROUTES.MARKETING_EMAIL_LICENCE_NEW
                        : APP_ROUTES.MARKETING_WEBSITE_NEW,
                    { state: { returnTab: activeTab } },
                  )
                }
              />
              {canViewExcel && (
                <PrimaryButton
                  label="Export to CSV"
                  onClick={handleDownloadExcel}
                />
              )}
            </div>
          </div>
        </header>

          {successMessage && <Banner type="success" message={successMessage} />}
          {error && <Banner type="error" message={error} />}

        <section className="website-dashboard-card table-scroll-card">
          <Tabs
            tabs={DASHBOARD_TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          {/* {error && (
            <div className="website-alert FP_Content" role="alert">
              {error}
            </div>
          )} */}
          {activeTab === "websites" && (
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table className="website-table">
                <thead>
                  <tr>
                    <th className="FP_Help_Text_Semibold table-index-column"></th>
                    <th className="FP_Help_Text_Semibold uppercase">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePipelineSort("adviser")}
                      >
                        Adviser <SortIcon aria-hidden />
                      </button>
                    </th>
                    <th className="FP_Help_Text_Semibold">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePipelineSort("activity")}
                      >
                        Dates <SortIcon aria-hidden />
                      </button>
                    </th>

                    <th className="FP_Help_Text_Semibold">AR/TS Name</th>
                    <th className="FP_Help_Text_Semibold">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePipelineSort("turnaround")}
                      >
                        Estimated Turnaround / Live Date <SortIcon aria-hidden />
                      </button>
                    </th>
                    <th className="FP_Help_Text_Semibold">Status</th>
                    <th className="FP_Help_Text_Semibold">Assigned To</th>
                    <th className="table-action-column" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="FP_Content primary" colSpan="8">
                        Loading websites…
                      </td>
                    </tr>
                  ) : pipelineWebsites.length ? (
                    pipelineWebsites.map((website, websiteIndex) => {
                      const liveWebsiteFlag = getLiveWebsiteChargeFlag(website);
                      return (
                      <tr
                        key={website.id}
                        className={
                          liveWebsiteFlag
                            ? "website-live-charge-warning-row"
                            : isWebsiteOverdue(website)
                            ? "website-overdue-row"
                            : isWebsiteTimelineExtended(website)
                              ? "website-extended-row"
                              : ""
                        }
                        tabIndex="0"
                        onClick={() => openWebsite(website.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") openWebsite(website.id);
                        }}
                      >
                        <td className="FP_Help_Text black table-index-column">
                          {websiteIndex + 1}.
                        </td>
                        <td className="FP_Help_Text black">
                          <div className="row align-center gap-s">
                            <strong>{website.adviserName || "-"}</strong>
                            {liveWebsiteFlag ? (
                              <Tooltip position="right" content={liveWebsiteFlag}>
                                <WarningIcon
                                  className="website-table__warning-icon"
                                  aria-label={liveWebsiteFlag}
                                />
                              </Tooltip>
                            ) : null}
                          </div>
                        </td>
                        <td className="FP_Help_Text black">
                          <div className="table-date-stack">
                            <span className="table-date-item">
                              <span>Informed</span>
                              <span
                                className="table-date-separator"
                                aria-hidden="true"
                              >
                                |
                              </span>
                              {formatDate(website.dateAdviserInformed)}
                            </span>
                            <span className="table-date-item">
                              <span>Started</span>
                              <span
                                className="table-date-separator"
                                aria-hidden="true"
                              >
                                |
                              </span>
                              {formatDate(website.dateStarted)}
                            </span>
                            <span className="table-date-item">
                              <span>Last Update</span>
                              <span
                                className="table-date-separator"
                                aria-hidden="true"
                              >
                                |
                              </span>
                              <strong>
                                {formatDate(website.updatedAt, true)}
                              </strong>
                            </span>
                          </div>
                        </td>

                        <td className="FP_Help_Text black">
                          {website.arTsName || "-"}
                        </td>
                        <td className="FP_Help_Text black">
                          {getPipelineDates(website).map((date) => formatDate(date)).join(", ") || "-"}
                        </td>
                        <td className="FP_Help_Text">
                          {website.status && (
                            <OutlinedStatusButton
                              label={website.status || ""}
                              color={STATUS_COLOURS[website.status] || "grey"}
                            />
                          )}
                        </td>
                        <td className="FP_Help_Text black">
                          {website.assignedTo || "-"}
                        </td>
                        <td className="table-action-column">
                          <Tooltip
                            position="left"
                            content={
                              String(website.notes || "").trim() ||
                              "No notes added."
                            }
                          >
                            <InfoIcon
                              className="website-table__notes-icon"
                              aria-label={`Latest notes for ${website.adviserName || "website"}`}
                            />
                          </Tooltip>
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="FP_Content primary" colSpan="8">
                        No website entries found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {activeTab === "charges" && (
            <WebsiteChargesTable
              websites={filteredWebsites}
              loading={loading}
              onOpen={openWebsite}
              showTotals={canViewChargeTotals}
              searchTerm={searchTerm}
            />
          )}
          {activeTab === "printing" && (
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table className="website-table printing-charges-table">
                <thead>
                  <tr>
                    <th className="FP_Help_Text_Semibold table-index-column" />
                    <th className="FP_Help_Text_Semibold">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePrintingSort("adviserName")}
                      >
                        Adviser <SortIcon aria-hidden />
                      </button>
                    </th>
                    <th className="FP_Help_Text_Semibold">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePrintingSort("orderItems")}
                      >
                        Order Items <SortIcon aria-hidden />
                      </button>
                    </th>
                    <th className="FP_Help_Text_Semibold">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePrintingSort("supplier")}
                      >
                        Supplier <SortIcon aria-hidden />
                      </button>
                    </th>
                    <th className="FP_Help_Text_Semibold">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePrintingSort("orderDate")}
                      >
                        Order Date <SortIcon aria-hidden />
                      </button>
                    </th>
                    <th className="FP_Help_Text_Semibold">
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => togglePrintingSort("amountToCharge")}
                      >
                        Amount to Charge <SortIcon aria-hidden />
                      </button>
                    </th>
                    <th className="FP_Help_Text_Semibold">Charge Across</th>
                    <th className="table-action-column" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {printingLoading ? (
                    <tr>
                      <td className="FP_Content primary" colSpan="8">
                        Loading printing charges…
                      </td>
                    </tr>
                  ) : sortedPrintingCharges.length ? (
                    sortedPrintingCharges.map((charge, index) => (
                      <tr
                        key={charge.id}
                        tabIndex="0"
                        onClick={() => openPrintingCharge(charge.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter")
                            openPrintingCharge(charge.id);
                        }}
                      >
                        <td className="FP_Help_Text black table-index-column">
                          {index + 1}.
                        </td>
                        <td className="FP_Help_Text black">
                          <strong>{charge.adviserName || "-"}</strong>
                        </td>
                        <td className="FP_Help_Text black">
                          {charge.orderItems?.join(", ") || "-"}
                        </td>
                        <td className="FP_Help_Text black">
                          {charge.supplier || "-"}
                        </td>
                        <td className="FP_Help_Text black">
                          {formatDate(charge.orderDate)}
                        </td>
                        <td className="FP_Help_Text black">
                          {charge.amountToCharge
                            ? `£${Number(charge.amountToCharge).toFixed(2)}`
                            : "-"}
                        </td>
                        <td className="FP_Help_Text black">
                          {charge.chargeAcross
                            ? `${charge.chargeAcross} month${Number(charge.chargeAcross) === 1 ? "" : "s"}`
                            : "-"}
                        </td>
                        <td className="table-action-column">
                          <button
                            type="button"
                            className="table-row-action"
                            aria-label={`View ${charge.adviserName || "printing charge"} details`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openPrintingCharge(charge.id);
                            }}
                          >
                            <ArrowRightIcon aria-hidden />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="FP_Content primary" colSpan="8">
                        No printing charge entries found.
                      </td>
                    </tr>
                  )}
                </tbody>
                {canViewChargeTotals && !printingLoading && sortedPrintingCharges.length > 0 && (
                  <tfoot>
                    <tr className="website-charges-total-row">
                      <td className="FP_Help_Text black" colSpan="5">Total</td>
                      <td className="FP_Help_Text black">£{printingChargesTotal.toFixed(2)}</td>
                      <td aria-hidden="true" />
                      <td aria-hidden="true" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          {activeTab === "licences" && (
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table className="website-table email-licences-table">
                <thead>
                  <tr>
                    <th className="table-index-column" />
                    {[
                      ["adviserName", "Adviser"],
                      ["licenceType", "Licence Description"],
                      ["numberOfLicences", "Number of Licences"],
                      ["orderDate", "Date Requested"],
                      ["chargeFromDate", "Charge From"],
                      ["monthlyAmount", "Monthly Charge"],
                    ].map(([key, label]) => (
                      <th key={key} className="FP_Help_Text_Semibold">
                        <button
                          type="button"
                          className="table-heading-content table-sort-button"
                          onClick={() => toggleLicenceSort(key)}
                        >
                          {label} <SortIcon aria-hidden />
                        </button>
                      </th>
                    ))}
                    <th className="table-action-column" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {licencesLoading ? (
                    <tr>
                      <td colSpan="8">Loading email licences…</td>
                    </tr>
                  ) : sortedEmailLicences.length ? (
                    sortedEmailLicences.map((licence, index) => (
                      <tr
                        key={licence.id}
                        tabIndex="0"
                        onClick={() => openEmailLicence(licence.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter")
                            openEmailLicence(licence.id);
                        }}
                      >
                        <td className="FP_Help_Text black table-index-column">
                          {index + 1}.
                        </td>
                        <td className="FP_Help_Text black">
                          <strong>{licence.adviserName || "-"}</strong>
                        </td>
                        <td className="FP_Help_Text black">
                          {licence.licenceType || "-"}
                        </td>
                        <td className="FP_Help_Text black">
                          {licence.numberOfLicences || "-"}
                        </td>
                        <td className="FP_Help_Text black">
                          {formatDate(licence.orderDate)}
                        </td>
                        <td className="FP_Help_Text black">
                          {formatDate(licence.chargeFromDate || licence.orderDate)}
                        </td>
                        <td className="FP_Help_Text black">
                          {licence.monthlyAmount
                            ? `£${Number(licence.monthlyAmount).toFixed(2)}`
                            : "-"}
                        </td>
                        <td className="table-action-column">
                          <button
                            type="button"
                            className="table-row-action"
                            aria-label={`View ${licence.adviserName || "email licence"} details`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openEmailLicence(licence.id);
                            }}
                          >
                            <ArrowRightIcon aria-hidden />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="FP_Help_Text black">
                        No email licence entries found.
                      </td>
                    </tr>
                  )}
                </tbody>
                {canViewChargeTotals && !licencesLoading && sortedEmailLicences.length > 0 && (
                  <tfoot>
                    <tr className="website-charges-total-row">
                      <td className="FP_Help_Text black" colSpan="6">Total</td>
                      <td className="FP_Help_Text black">£{emailLicencesTotal.toFixed(2)}</td>
                      <td aria-hidden="true" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default MarketingWebsitesDashboard;
